import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightsBreakdown } from '@/api/assistantInsights/assistantInsightsApi.types';
import { OptionChips } from '@/components/assistantConfiguration/OptionChips';
import { Card } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/datePicker';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useAssistantInsightsWorkspacesQuery } from '@/hooks/useAssistantInsights';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { isKnownTopic } from './topicPalette';
import {
  USAGE_PERIODS,
  USAGE_TOPICS,
  toInsightsFilters,
  type UsageFilterState,
} from './usageFilters';
import { useUsageLabels } from './useUsageLabels';

interface UsageFilterBarProps {
  state: UsageFilterState;
  screens: InsightsBreakdown[];
  today: Date;
  onChange: (nextState: UsageFilterState) => void;
}

export function UsageFilterBar({ state, screens, today, onChange }: UsageFilterBarProps) {
  const { t } = useTranslation('assistant');
  const { topicLabel, screenLabel } = useUsageLabels();
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const debouncedWorkspaceSearch = useDebouncedValue(workspaceSearch, SEARCH_DEBOUNCE_MILLISECONDS);
  const workspaceOptionsQuery = useAssistantInsightsWorkspacesQuery(
    toInsightsFilters({ ...state, tenantId: null, tenantName: null }, today),
    1,
    debouncedWorkspaceSearch
  );
  const workspaceOptions = (
    workspaceOptionsQuery.data === undefined ? [] : workspaceOptionsQuery.data.items
  ).map(function toOption(workspace) {
    return { value: workspace.tenantId, label: workspace.tenantName };
  });
  if (state.tenantId !== null && state.tenantName !== null) {
    const isListed = workspaceOptions.some(function matches(option) {
      return option.value === state.tenantId;
    });
    if (isListed === false) {
      workspaceOptions.unshift({ value: state.tenantId, label: state.tenantName });
    }
  }
  const screenKeys = screens.map(function readKey(screen) {
    return screen.key;
  });
  if (state.screen !== null && screenKeys.includes(state.screen) === false) {
    screenKeys.unshift(state.screen);
  }

  return (
    <Card padding="sm">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
        <div className="space-y-3">
          <OptionChips
            label={t('insights.filters.period')}
            options={USAGE_PERIODS.map(function toOption(period) {
              return { value: period, label: t('insights.filters.periods.' + period) };
            })}
            selectedValue={state.period}
            onSelect={function selectPeriod(period) {
              onChange({ ...state, period });
            }}
          />
          {state.period === 'custom' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
              <div className="space-y-1.5">
                <Label htmlFor="usage-date-from">{t('insights.filters.from')}</Label>
                <DatePicker
                  id="usage-date-from"
                  value={state.customFrom}
                  onChange={function updateFrom(value) {
                    onChange({ ...state, customFrom: value });
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-date-to">{t('insights.filters.to')}</Label>
                <DatePicker
                  id="usage-date-to"
                  value={state.customTo}
                  onChange={function updateTo(value) {
                    onChange({ ...state, customTo: value });
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3 gap-3 content-start">
          <div className="space-y-1.5">
            <Label htmlFor="usage-workspace">{t('insights.filters.workspace')}</Label>
            <SearchableSelect
              inputId="usage-workspace"
              options={workspaceOptions}
              value={state.tenantId === null ? '' : state.tenantId}
              isClearable
              isLoading={workspaceOptionsQuery.isFetching}
              placeholder={t('insights.filters.allWorkspaces')}
              noOptionsMessage={t('insights.workspaces.noMatch')}
              onInputChange={setWorkspaceSearch}
              onChange={function selectWorkspace(value) {
                const option = workspaceOptions.find(function matches(candidate) {
                  return candidate.value === value;
                });
                onChange({
                  ...state,
                  tenantId: value.length === 0 ? null : value,
                  tenantName: option === undefined ? null : option.label,
                });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="usage-topic">{t('insights.filters.topic')}</Label>
            <SearchableSelect
              inputId="usage-topic"
              options={USAGE_TOPICS.map(function toOption(topic) {
                return { value: topic, label: topicLabel(topic) };
              })}
              value={state.topic === null ? '' : state.topic}
              isClearable
              placeholder={t('insights.filters.allTopics')}
              onChange={function selectTopic(value) {
                onChange({ ...state, topic: isKnownTopic(value) ? value : null });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="usage-screen">{t('insights.filters.screen')}</Label>
            <SearchableSelect
              inputId="usage-screen"
              options={screenKeys.map(function toOption(screen) {
                return { value: screen, label: screenLabel(screen) };
              })}
              value={state.screen === null ? '' : state.screen}
              isClearable
              placeholder={t('insights.filters.allScreens')}
              onChange={function selectScreen(value) {
                onChange({ ...state, screen: value.length === 0 ? null : value });
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
